/**
 * P-02b (D-26, plan §6.1–§6.2) — the pure rules of the "where does this task
 * live" chain: customer* → engagement type* → engagement* → milestone (only on
 * a project / development). Shared by the task form, the new-ticket form, the
 * timer and "רישום שעות". No React, no requests: pinned by
 * `src/utils/__tests__/engagementChainModel.test.ts`.
 *
 * - Changing a field clears every field below it (`setChainField`).
 * - A level with exactly one option is picked automatically (`autoPickChain`).
 * - Locked levels (filled from the screen the form was opened on) are shown as
 *   text and are never cleared or auto-changed (`ChainLock`).
 */
import { ENGAGEMENT_TYPE_ORDER, isProjectEngagementType } from '../../utils/engagementLabel';

export interface ChainValue {
  organizationId: string;
  engagementType: string;
  engagementId: string;
  milestoneId: string;
}

export const EMPTY_CHAIN: ChainValue = { organizationId: '', engagementType: '', engagementId: '', milestoneId: '' };

export type ChainField = keyof ChainValue;
/** Top-down order of the chain. */
export const CHAIN_ORDER: ChainField[] = ['organizationId', 'engagementType', 'engagementId', 'milestoneId'];

/**
 * How far down the chain is fixed by the screen: 'customer' locks the
 * customer; 'engagement' locks customer + type + engagement; 'milestone'
 * locks everything (the milestone included, even when it is "none").
 */
export type ChainLock = 'customer' | 'engagement' | 'milestone' | undefined;

export function isLocked(field: ChainField, lock: ChainLock): boolean {
  if (!lock) return false;
  const depth = lock === 'customer' ? 1 : lock === 'engagement' ? 3 : 4;
  return CHAIN_ORDER.indexOf(field) < depth;
}

/** Set one field and clear every field below it. */
export function setChainField(value: ChainValue, field: ChainField, next: string): ChainValue {
  const out: ChainValue = { ...value, [field]: next };
  for (const f of CHAIN_ORDER.slice(CHAIN_ORDER.indexOf(field) + 1)) out[f] = '';
  return out;
}

/** The row shape `GET /engagements` returns (only what the chain reads). */
export interface ChainEngagement {
  id: string;
  type: string;
  organization_id: string;
  organization_name?: string | null;
  child_name?: string | null;
  child_status?: string | null;
  project_id?: string | null;
  deleted_at?: string | null;
  /** P-33a (D-185) — the company's "עבודה פנימית". */
  is_internal_work?: boolean;
  organization_is_internal?: boolean;
}

/**
 * P-33a (D-185 / D-188) — the task picker's first option, "OTE, עבודה פנימית":
 * the company and its "עבודה פנימית" engagement in one pick.
 */
export interface InternalWorkOption {
  organizationId: string;
  engagementId: string;
}

/**
 * The customer list of the picker: "OTE, עבודה פנימית" first, then the
 * customers (the company is never listed as a customer, D-188).
 */
export function withInternalWorkFirst<T extends { id: string; name: string }>(
  organizations: readonly T[],
  internal: InternalWorkOption | null | undefined,
  label: string
): Array<{ id: string; name: string; isInternalWork?: boolean }> {
  const rest = organizations.filter((o) => !internal || o.id !== internal.organizationId)
    .map((o) => ({ id: o.id, name: o.name }));
  return internal ? [{ id: internal.organizationId, name: label, isInternalWork: true }, ...rest] : rest;
}

/**
 * Of the company only "עבודה פנימית" is offered (D-188): its older engagements
 * (e.g. 02200 / P-04) are not in the task picker. Other customers: unchanged.
 */
export function restrictToInternalWork(
  engagements: readonly ChainEngagement[],
  organizationId: string,
  internal: InternalWorkOption | null | undefined,
  /** D-274 — an engagement fixed by the screen (an internal project's page) stays listed. */
  keepId?: string
): ChainEngagement[] {
  if (!internal || organizationId !== internal.organizationId) return [...engagements];
  return engagements.filter((e) => e.id === internal.engagementId || e.is_internal_work === true || (!!keepId && e.id === keepId));
}

/** D-274 §3 — one open project of "עבודה פנימית OTE" (GET /engagements/internal-projects). */
export interface InternalProjectChoice {
  project_id: string;
  engagement_id: string;
  engagement_type: string;
  name: string;
  milestones: ReadonlyArray<{ id: string; name: string; parent_milestone_id: string | null }>;
}

/** D-274 §3 — the chain's customer is "עבודה פנימית OTE". */
export function isCompanyChain(value: ChainValue, internal: InternalWorkOption | null | undefined): boolean {
  return !!internal && !!value.organizationId && value.organizationId === internal.organizationId;
}

/**
 * D-274 §3 — "פרויקט (לא חובה)": null = "ישירות תחת OTE" ("עבודה פנימית"),
 * else that project's engagement. The milestone is cleared (a new choice).
 */
export function pickInternalProject(
  project: InternalProjectChoice | null,
  internal: InternalWorkOption
): ChainValue {
  return project
    ? { organizationId: internal.organizationId, engagementType: project.engagement_type, engagementId: project.engagement_id, milestoneId: '' }
    : { organizationId: internal.organizationId, engagementType: 'development', engagementId: internal.engagementId, milestoneId: '' };
}

/** The internal project the chain points at, or null ("ישירות תחת OTE"). */
export function selectedInternalProject<T extends InternalProjectChoice>(value: ChainValue, projects: readonly T[]): T | null {
  return projects.find((p) => p.engagement_id === value.engagementId) ?? null;
}

/**
 * Picking "OTE, עבודה פנימית" fills the company AND "עבודה פנימית" at once
 * (type development, no milestone). Any other customer: a plain setChainField.
 */
export function pickCustomer(value: ChainValue, organizationId: string, internal: InternalWorkOption | null | undefined): ChainValue {
  const next = setChainField(value, 'organizationId', organizationId);
  if (internal && organizationId && organizationId === internal.organizationId) {
    return { ...next, engagementType: 'development', engagementId: internal.engagementId, milestoneId: '' };
  }
  return next;
}

/**
 * P-33c (D-185) — an internal-only employee never picks where a task lives:
 * the chain is always the company + type development + "עבודה פנימית".
 */
export function internalOnlyChain(organizationId: string | null | undefined, engagementId: string | null | undefined): ChainValue | null {
  if (!organizationId || !engagementId) return null;
  return { organizationId, engagementType: 'development', engagementId, milestoneId: '' };
}

/** An engagement is open for new work while its typed child is planning / active / on hold. */
export const OPEN_ENGAGEMENT_STATUSES = ['planning', 'active', 'on_hold'];

export function isOpenEngagement(e: ChainEngagement): boolean {
  return !e.deleted_at && !!e.child_status && OPEN_ENGAGEMENT_STATUSES.includes(e.child_status);
}

/** Types the customer has an OPEN engagement of, in the fixed order. */
export function typesWithOpenEngagement(engagements: readonly ChainEngagement[]): string[] {
  const present = new Set(engagements.filter(isOpenEngagement).map((e) => e.type));
  return ENGAGEMENT_TYPE_ORDER.filter((ty) => present.has(ty));
}

/** Open engagements of one type (the engagement picker), keeping `keepId` even when it is not open. */
export function engagementsOfType(engagements: readonly ChainEngagement[], type: string, keepId?: string): ChainEngagement[] {
  return engagements.filter((e) => e.type === type && (isOpenEngagement(e) || (!!keepId && e.id === keepId)));
}

/**
 * The new-ticket form's single engagement picker (every type): OPEN
 * engagements only (planning / active / on hold), like the task form. The
 * exception: `keepId` — the ticket's own engagement (editing an existing
 * ticket, or one pre-set by the screen) — stays listed even when closed.
 */
export function openEngagements(engagements: readonly ChainEngagement[], keepId?: string): ChainEngagement[] {
  return engagements.filter((e) => isOpenEngagement(e) || (!!keepId && e.id === keepId));
}

/** The milestone picker is shown only on a project / development. */
export function chainHasMilestone(type: string): boolean {
  return isProjectEngagementType(type);
}

export interface ChainOptions {
  organizations: readonly { id: string }[];
  types: readonly string[];
  engagements: readonly { id: string }[];
}

/**
 * Auto-pick: a level whose value is empty and that has exactly one option
 * takes it. Locked levels are left alone. Runs top-down once, so a single
 * customer with a single engagement fills the whole chain in one pass only
 * when the options of the lower levels are already known (callers re-run it
 * as data arrives). The milestone is never auto-picked ("none" is a choice).
 * `withType = false`: there is no type level (the new-ticket form lists every
 * engagement of the customer in one picker).
 */
export function autoPickChain(value: ChainValue, opts: ChainOptions, lock: ChainLock, withType = true): ChainValue {
  let out = value;
  if (!out.organizationId && !isLocked('organizationId', lock) && opts.organizations.length === 1) {
    out = setChainField(out, 'organizationId', opts.organizations[0].id);
  }
  if (withType && out.organizationId && !out.engagementType && !isLocked('engagementType', lock) && opts.types.length === 1) {
    out = setChainField(out, 'engagementType', opts.types[0]);
  }
  // Without a type level (the new-ticket form), the engagement follows the customer directly.
  if ((withType ? out.engagementType : out.organizationId) && !out.engagementId && !isLocked('engagementId', lock) && opts.engagements.length === 1) {
    out = setChainField(out, 'engagementId', opts.engagements[0].id);
  }
  return out;
}

export type Translate = (key: string, params?: Record<string, unknown>) => string;

/** Errors of the chain (control ids `<prefix>-customer` / `-type` / `-engagement`). */
export function validateChain(value: ChainValue, t: Translate, prefix = 'task', withType = true): Record<string, string | undefined> {
  const errors: Record<string, string | undefined> = {};
  if (!value.organizationId) errors[`${prefix}-customer`] = t('engagementChain.errors.customerRequired');
  else if (withType && !value.engagementType) errors[`${prefix}-type`] = t('engagementChain.errors.typeRequired');
  else if (!value.engagementId) errors[`${prefix}-engagement`] = t('engagementChain.errors.engagementRequired');
  return errors;
}
